import type { MaybeRef } from 'vue';
import type { CountryResponse } from '../stores/countryStore';
import type { Address, PhoneNumber } from '../types/account';
import type { UserAccountType } from '../types/enums';
import { isRef } from 'vue';
import { date, number, string } from 'yup';
import { validateTaxId } from '../composables/taxIdValidator';
import { consoleLog, yupObject } from './utils';

interface yupAddressSchemaParams {
	accountType: MaybeRef<UserAccountType | undefined>;
	countries: CountryResponse[];
}

export function yupStateSchema(countries: CountryResponse[], countryPropertyPath = 'country.iso2Code') {
	return string().nullable().test('is-valid-state', 'Invalid state', function (value) {
		consoleLog('Running state validation for value:', value, 'with countryPropertyPath:', countryPropertyPath);
		const parent = this.parent as Record<string, any>;
		const getPathValue = (obj: Record<string, any> | undefined, path: string) =>
			path.split('.').reduce((current, key) => current?.[key], obj);

		let country = parent[countryPropertyPath];
		if (!country && !countryPropertyPath.includes('.')) {
			return true;
		}
		else if (!country && countryPropertyPath.includes('.')) {
			country = getPathValue(parent, countryPropertyPath);

			if (!country) {
				const pathParts = countryPropertyPath.split('.');
				if (pathParts.length > 1) {
					country = getPathValue(parent, pathParts.slice(1).join('.'));
				}
			}

			if (!country) {
				return true;
			}
		}

		const selectedCountry = countries.find(
			c => c.twoLetterCountryCode === country,
		);

		if (import.meta.dev) {
			consoleLog(
				'Validating state with value:',
				value,
				'for country:',
				country,
				'selectedCountry:',
				selectedCountry,
			);
		}

		if (!selectedCountry || selectedCountry.stateValidation === 'Optional') {
			return true;
		}

		const stateName = selectedCountry.stateUnitName || 'state';
		const isValidState = selectedCountry.states?.some(s => s.stateCode === value) ?? false;

		const isOptionalValidated = selectedCountry.stateValidation === 'OptionalAndValidated';
		const isRequiredValidated = selectedCountry.stateValidation === 'RequiredAndValidated';

		if (isOptionalValidated && value && !isValidState) {
			return this.createError({ message: `Please select a valid ${stateName}.` });
		}

		if (isRequiredValidated && (!value || !isValidState)) {
			return this.createError({ message: `Please select a valid ${stateName}.` });
		}

		return true;
	});
}

/**
 * Generates a Yup validation schema for an address object.
 *
 * @param {yupAddressSchemaParams} params - The parameters for the schema.
 * @param {string | Ref<string>} params.accountType - The type of account, which determines
 * whether the `companyName` field is required. If the account type is 'Business',
 * the `companyName` field will be required; otherwise, it will be nullable.
 *
 * @returns {Yup.ObjectSchema<Address>} A Yup object schema for validating an address.
 */
export function yupAddressSchema({ accountType, countries }: yupAddressSchemaParams) {
	return yupObject<Address>({
		streetAddress: string().required().max(50),
		city: string().required().max(50),
		state: yupStateSchema(countries, 'country.iso2Code'),
		country: yupObject<Address['country']>({
			iso2Code: string().required(),
		}).required(),
		name: yupObject<Address['name']>({
			firstName: string().required().max(50),
			lastName: string().required().max(50),
		}),
		companyName: string().when({
			is: () => (isRef(accountType) ? accountType.value : accountType) === 'Business',
			then: () => string().required().max(100),
			otherwise: () => string().nullable().max(100),
		}),
		department: string().nullable().max(50),
		postalCode: string().required().max(20),
		streetAddress2: string().nullable().max(50),
	});
}

interface yupPhoneNumberSchemaParams {
	required?: MaybeRef<boolean>;
}

/**
 * Generates a Yup validation schema for a phone number object.
 *
 * @param {yupPhoneNumberSchemaParams} params - The parameters for the schema.
 * @param {boolean | Ref<boolean>} params.required - Indicates whether the phone number
 * is required. If true, the `number` field will be required; otherwise, it will be nullable.
 *
 * @returns {Yup.ObjectSchema<PhoneNumber>} A Yup object schema for validating a phone number.
 */
export function yupPhoneNumberSchema({ required }: yupPhoneNumberSchemaParams) {
	return yupObject<PhoneNumber>({
		number: string()
			.max(12)
			.when({
				is: () => (isRef(required) ? required.value : required) === true,
				then: schema => schema.required(),
				otherwise: schema => schema.nullable(),
			})
			.test(
				'is-numeric',
				'Phone number can only contain digits, spaces, dashes (-), and parentheses ()',
				value => !value || /^[\d\s\-()]+$/.test(value),
			),
		countryIso2Code: string().required().max(5),
	});
}

// eslint-disable-next-line regexp/no-unused-capturing-group
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-z\-0-9]+\.)+[a-z]{2,}))$/i;

export function yupEmailSchema(params: { required?: MaybeRef<boolean> } = {}) {
	return string()
		.max(254)
		.when({
			is: () => (isRef(params.required) ? params.required.value : params.required) === true,
			then: schema => schema.required(),
			otherwise: schema => schema.nullable(),
		})
		.test('is-valid-email', 'Please enter a valid email address.', value => !value || emailRegex.test(value));
}

interface yupDateSchemaParams {
	required?: MaybeRef<boolean>;
	allowPast?: MaybeRef<boolean>;
	allowToday?: MaybeRef<boolean>;
	allowFuture?: MaybeRef<boolean>;
}

/**
 * Generates a Yup validation schema for a date field with optional time-based constraints.
 *
 * @param {yupDateSchemaParams} params - The parameters for the schema.
 * @param {boolean | Ref<boolean>} params.required - Indicates whether the date is required.
 * @param {boolean | Ref<boolean>} params.allowPast - Allows dates before today.
 * @param {boolean | Ref<boolean>} params.allowToday - Allows today's date.
 * @param {boolean | Ref<boolean>} params.allowFuture - Allows dates after today.
 *
 * The boolean flags can be combined:
 *   - allowPast only: Date must be before today (midnight)
 *   - allowFuture only: Date must be after today (end of day)
 *   - allowToday only: Date must be within today
 *   - allowPast + allowToday: Date must be today or in the past
 *   - allowFuture + allowToday: Date must be today or in the future
 *
 * @returns {Yup.DateSchema} A Yup date schema with appropriate validation rules.
 */
export function yupDateSchema({ required, allowPast, allowToday, allowFuture }: yupDateSchemaParams = {}) {
	const hasPast = () => (isRef(allowPast) ? allowPast.value : allowPast) === true;
	const hasToday = () => (isRef(allowToday) ? allowToday.value : allowToday) === true;
	const hasFuture = () => (isRef(allowFuture) ? allowFuture.value : allowFuture) === true;

	return date()
		.transform((value, originalValue) => {
			if (typeof originalValue === 'string') {
				return new Date(originalValue);
			}
			return value;
		})
		.when({
			is: () => (isRef(required) ? required.value : required) === true,
			then: d => d.required(),
			otherwise: d => d.nullable(),
		})
		.when({
			// Only past (no today, no future)
			is: () => hasPast() && !hasToday() && !hasFuture(),
			then: d => d.max(new Date(new Date().setHours(0, 0, 0, 0)), 'Date must be in the past'),
		})
		.when({
			// Only future (no today, no past)
			is: () => hasFuture() && !hasToday() && !hasPast(),
			then: d => d.min(new Date(new Date().setHours(23, 59, 59, 999)), 'Date must be in the future'),
		})
		.when({
			// Only today (no past, no future)
			is: () => hasToday() && !hasPast() && !hasFuture(),
			then: d => d.min(new Date(new Date().setHours(0, 0, 0, 0)), 'Date must be today')
				.max(new Date(new Date().setHours(23, 59, 59, 999)), 'Date must be today'),
		})
		.when({
			// Past + Today (no future) - allow up to end of today
			is: () => hasPast() && hasToday() && !hasFuture(),
			then: d => d.max(new Date(new Date().setHours(23, 59, 59, 999)), 'Date must be today or in the past'),
		})
		.when({
			// Future + Today (no past) - allow from start of today
			is: () => hasFuture() && hasToday() && !hasPast(),
			then: d => d.min(new Date(new Date().setHours(0, 0, 0, 0)), 'Date must be today or in the future'),
		});
}

export const NET_PRICE_MIN = 500;
export const NET_PRICE_MAX = 2000000;

interface yupNetPriceSchemaParams {
	required?: MaybeRef<boolean>;
}

export function yupNetPriceSchema({ required }: yupNetPriceSchemaParams = {}) {
	return number()
		.transform((value, originalValue) => (originalValue === '' ? null : value))
		.integer()
		.min(NET_PRICE_MIN, `Price can be set to min. ${NET_PRICE_MIN} EUR.`)
		.max(NET_PRICE_MAX, 'Price can be set to max. 2.000.000 EUR.')
		.when({
			is: () => (isRef(required) ? required.value : required) === true,
			then: schema => schema.required(),
			otherwise: schema => schema.nullable(),
		});
}

export function yupTaxIdSchema(params: { required?: MaybeRef<boolean>; countryCode: MaybeRef<string | undefined | null> }) {
	return string()
		.max(20)
		.when({
			is: () => (isRef(params.required) ? params.required.value : params.required) === true,
			then: schema => schema.required(),
			otherwise: schema => schema.nullable(),
		})
		.test('is-valid-tax-id', 'Invalid Tax ID', (value) => {
			if (!value || value.length === 0 || !params.countryCode) {
				return true;
			}

			return validateTaxId(value, isRef(params.countryCode!) ? params.countryCode.value! : params.countryCode!);
		});
}
